import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Loader, useToasts } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { convertQuillHTMLToText, timeFormats } from 'shared/utils';
import { TimeZone, UserType } from '../../../shared/types';
import Icon from '../../../shared/components/Icon';
import Spacer from '../../../shared/components/Spacer';
import { timeZones } from '../../../shared/constants';
import { useGetAccountInfo } from '../../../shared/hooks';
import {
  Center,
  FormActions,
  FormContainer,
  FormContent,
  FormField,
  FormHeader,
  FormSection,
} from '../../../shared/styles';
import { useGetCurrentUser, useUpdateUserInfo } from '../settings/hooks';
import { SettingsFixedDrawer } from '../settings/styles';
import { Container, ProfileContainer } from './style';

const supportedLanguages = [{ id: 'en', label: 'English' }];

const ProfileSettings = () => {
  const { t } = useTranslation();
  const [, setToast] = useToasts();
  const [selectedTimezone, setSelectedTimezone] = useState<string>('Etc/GMT');
  const { data: accountinfo } = useGetAccountInfo({
    enabled: true,
  });
  const { data: loggedInUser, isLoading: loggedInUserLoading } =
    useGetCurrentUser({});
  const { mutate: updateUserInfo } = useUpdateUserInfo({ setToast });
  const freshidURL = `https://${
    accountinfo?.organisation_domain
  }/profile?redirect_uri=${encodeURIComponent(window.location.origin)}`;

  const form = useForm<UserType>({
    shouldFocusError: true,
    mode: 'onSubmit',
    resolver: yupResolver(
      Yup.object({
        signature: Yup.string().test({
          test: function (text, context) {
            const { createError }: Yup.TestContext = context || {};
            if (convertQuillHTMLToText(text || '').length > 1500)
              return createError({
                message: `${t('forms.err_msg.min_char', {
                  len: 1500,
                })}`,
              });
            return true;
          },
        }),
      }),
    ),
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = form;

  const onSubmit = (data) => {
    updateUserInfo({ ...loggedInUser, ...data });
  };

  useEffect(() => {
    if (loggedInUser?.signature) {
      reset(loggedInUser);
    }
  }, [loggedInUser]);

  const { first_name, middle_name, last_name, email } = loggedInUser || {};

  const translatedTimeFormats = timeFormats.map((f) => ({
    ...f,
    label: `${t(f.label)}`,
  }));

  return (
    <ProfileContainer>
      <FormContainer className="profileForm">
        <FormHeader hideBorder>
          <div className="heading">{t('profile.settings_title')}</div>
        </FormHeader>
        {loggedInUserLoading ? (
          <FormContent>
            <Center>
              <Loader />
            </Center>
          </FormContent>
        ) : (
          <FormContent>
            <Container
              alphabet={
                first_name
                  ? first_name.charAt(0).toUpperCase()
                  : last_name
                  ? last_name.charAt(0).toUpperCase()
                  : (email || '').charAt(0).toUpperCase()
              }
            >
              <FormSection>
                <div className="highlight">
                  <div>
                    <div className="profile-icon">
                      {first_name
                        ? first_name.charAt(0).toUpperCase()
                        : last_name
                        ? last_name.charAt(0).toUpperCase()
                        : (email || '').charAt(0).toUpperCase()}
                    </div>
                    <Spacer y={0.3} />
                    {/* <a href={freshidURL} rel="noreferrer" target="_blank">
                    Upload new
                  </a> */}
                  </div>
                  <div>
                    <p className="fullName">{`${first_name || ''} ${
                      middle_name || ''
                    } ${last_name || ''}`}</p>
                    <div className="email">{loggedInUser?.email}</div>
                  </div>
                </div>
              </FormSection>
              <FormSection>
                <div className="moreinfo">
                  <div className="item">
                    <div className="title">{t('first_name')}</div>
                    <div className="value">
                      {loggedInUser?.first_name || '-'}
                    </div>
                  </div>
                  <div className="item">
                    <div className="title">{t('middle_name')}</div>
                    <div className="value">
                      {loggedInUser?.middle_name || '-'}
                    </div>
                  </div>
                  <div className="item">
                    <div className="title">{t('last_name')}</div>
                    <div className="value">
                      {loggedInUser?.last_name || '-'}
                    </div>
                  </div>
                  <div className="item">
                    <div className="title">{t('company_name')}</div>
                    <div className="value">
                      {loggedInUser?.company_name || '-'}
                    </div>
                  </div>
                  <div className="item">
                    <div className="title">{t('job_title')}</div>
                    <div className="value">
                      {loggedInUser?.job_title || '-'}
                    </div>
                  </div>
                  <div className="item">
                    <div className="title">{t('mobile')}</div>
                    <div className="value">{loggedInUser?.mobile || '-'}</div>
                  </div>
                </div>
                <FormField>
                  <div className="helpbanner">
                    <Trans
                      i18nKey="profile.help_banner"
                      components={{
                        1: (
                          <a
                            href={freshidURL}
                            target="_blank"
                            rel="noreferrer"
                          />
                        ),
                      }}
                    />
                  </div>
                  <div className="hint">
                    <Icon width="11" height="11" glyph="info" />
                    &nbsp; {t('profile.hint')}
                  </div>
                </FormField>
              </FormSection>
              {/* <FormSection>
                <FormField>
                  <div className="custom-label">Signature</div>
                  <StyledSignature>
                    <QuillEditor
                      placeholders={[]}
                      id="quill-signature"
                      getValueAs="HTML"
                      isErrored={!!errors.signature?.message}
                      errorText={errors.signature?.message || ''}
                      showActions
                      placeholder="Email signature"
                      value={watch('signature') || ''}
                      onChange={(val: string) => {
                        setValue('signature', val, { shouldValidate: true });
                      }}
                      maxCharLimit={1500}
                    />
                  </StyledSignature>
                </FormField>
              </FormSection> */}
            </Container>
          </FormContent>
        )}
        <FormActions>
          <Button
            inline
            type="primary"
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.preventDefault();
              handleSubmit(onSubmit, (err) => console.log('ERR', err))();
            }}
            disabled
          >
            {t('save')}
          </Button>
        </FormActions>
      </FormContainer>
      <SettingsFixedDrawer>
        <FormField>
          <Dropdown
            isRequired
            label={`${t('language')}`}
            defaultSelectedItem={supportedLanguages[0]}
            emptyItemsText={`${t('no_results_found')}`}
            itemToString={(v: any) => v.label}
            itemComponent={(v: any) => v.label}
            items={supportedLanguages}
            disabled
          />
        </FormField>
        <FormField>
          <Dropdown
            type="input"
            filterKey="timeZone"
            label={`${t('timezone')}`}
            disabled
            isRequired
            emptyItemsText={`${t('no_results_found')}`}
            placeholder={`${t('forms.placeholders.timezone')}`}
            itemToString={(v: TimeZone) => `${v.timeZone} (${v.offset})`}
            itemComponent={(v: TimeZone) => `${v.timeZone} (${v.offset})`}
            defaultSelectedItem={
              timeZones.find(
                (item: TimeZone) => selectedTimezone === item.timeZone,
              ) || null
            }
            onChange={(option: TimeZone) => {
              if (!option) return;
              setSelectedTimezone(option.timeZone);
            }}
            items={timeZones}
          />
        </FormField>
        <FormField>
          <Dropdown
            isRequired
            label={`${t('time_format')}`}
            defaultSelectedItem={translatedTimeFormats[0]}
            itemToString={(v: any) => v.label}
            emptyItemsText={`${t('no_results_found')}`}
            itemComponent={(v: any) => v.label}
            items={translatedTimeFormats}
            disabled
          />
        </FormField>
      </SettingsFixedDrawer>
    </ProfileContainer>
  );
};
export default ProfileSettings;
